import { useEffect, useRef, useState } from "react";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { getBillingReturnUrl } from "../../../redux/actions/billing";
import { initCampaignFeePayment } from "../../../api/growthEngine/campaignFee.api";
import { formatUsdFee } from "../../../queries/growthEngine/campaignFee.query";
import { showToast } from "../../../helper/ShowToast";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

export const cleanupOrphanedStripeOverlays = () => {
    if (typeof document === "undefined") return;

    document.querySelectorAll("iframe").forEach((iframe) => {
        const name = iframe.getAttribute("name") || "";
        const src = iframe.getAttribute("src") || "";
        const isStripe =
            name.includes("privateStripeFrame") ||
            src.includes("js.stripe.com") ||
            src.includes("hooks.stripe.com") ||
            src.includes("stripe.com");
        if (!isStripe) return;
        const parent = iframe.parentElement;
        if (parent === document.body || parent === document.documentElement) {
            iframe.remove();
        }
    });

    if (document.body.style.overflow === "hidden") {
        document.body.style.overflow = "";
    }
    if (document.documentElement.style.overflow === "hidden") {
        document.documentElement.style.overflow = "";
    }
    if (document.body.style.pointerEvents === "none") {
        document.body.style.pointerEvents = "";
    }
};

export const CampaignFeePayModal = ({ isOpen, quote, onClose, onPaid }) => {
    const [clientSecret, setClientSecret] = useState(null);
    const [customerSessionClientSecret, setCustomerSessionClientSecret] = useState(null);
    const [initLoading, setInitLoading] = useState(false);
    const [paying, setPaying] = useState(false);
    const [initError, setInitError] = useState("");
    const onPaidRef = useRef(onPaid);
    const paidRef = useRef(false);

    onPaidRef.current = onPaid;

    const feeLabel = formatUsdFee(quote?.finalCampaignFeeUSD);
    const quoteId = quote?.quoteId;

    useEffect(() => {
        if (!isOpen || !quoteId) {
            setClientSecret(null);
            setCustomerSessionClientSecret(null);
            setPaying(false);
            cleanupOrphanedStripeOverlays();
            return undefined;
        }

        let cancelled = false;
        paidRef.current = false;
        setClientSecret(null);
        setCustomerSessionClientSecret(null);
        setInitError("");
        setInitLoading(true);

        (async () => {
            try {
                const res = await initCampaignFeePayment({ quoteId });
                if (cancelled) return;
                if (res?.feeRequired === false) {
                    setInitError("An active subscription is already on this account.");
                    return;
                }
                if ((res?.alreadyPaid || res?.alreadyAuthorized) && res?.paymentIntentId) {
                    if (!paidRef.current) {
                        paidRef.current = true;
                        onPaidRef.current(res.paymentIntentId);
                    }
                    return;
                }
                if (!res?.clientSecret || !res?.customerSessionClientSecret) {
                    setInitError("Failed to initialize Stripe payment.");
                    return;
                }
                setClientSecret(res.clientSecret);
                setCustomerSessionClientSecret(res.customerSessionClientSecret);
            } catch (err) {
                if (cancelled) return;
                setInitError(
                    err?.response?.data?.message || "Failed to initialize campaign fee payment."
                );
            } finally {
                if (!cancelled) setInitLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            cleanupOrphanedStripeOverlays();
        };
    }, [isOpen, quoteId]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm overflow-auto">
            <div className="bg-gradient-to-br from-[#000456] via-[#0a1a8a] to-[#1e3a8a] rounded-2xl shadow-lg w-full max-w-[640px] mx-2 my-10">
                <div className="modal-body relative px-4 md:px-6 py-8">
                    <button
                        type="button"
                        onClick={onClose}
                        className="absolute top-3 right-4 text-theme_clr2 hover:text-theme_clr4 text-xl"
                    >
                        <i className="bi bi-x-lg"></i>
                    </button>

                    <div className="text-center mb-6">
                        <h2 className="text-2xl font-bold text-white mb-2">Confirm & Pay</h2>
                        <p className="text-base text-gray-300 max-w-xl mx-auto">
                            Review the UpNComer campaign fee and payment details before launching your campaign.
                        </p>
                        <div className="mt-4 text-white text-xl font-bold">{feeLabel}</div>
                    </div>

                    {initError ? (
                        <div className="bg-white/5 p-6 rounded-xl border border-white/10 text-center">
                            <p className="text-red-300 mb-4">{initError}</p>
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-6 py-3 rounded-lg font-bold text-white bg-theme_clr2 hover:bg-[#e55532]"
                            >
                                Close
                            </button>
                        </div>
                    ) : initLoading || !clientSecret || !customerSessionClientSecret ? (
                        <div className="flex justify-center py-10">
                            <span className="h-8 w-8 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        </div>
                    ) : (
                        <Elements
                            key={clientSecret}
                            stripe={stripePromise}
                            options={{
                                clientSecret,
                                customerSessionClientSecret,
                                appearance: {
                                    theme: "flat",
                                    variables: { colorPrimary: "#FA6138" },
                                },
                                loader: "auto",
                            }}
                        >
                            <CampaignFeePaymentForm
                                feeLabel={feeLabel}
                                loading={paying}
                                setLoading={setPaying}
                                paidRef={paidRef}
                                onPaid={(paymentIntentId) => onPaidRef.current(paymentIntentId)}
                            />
                        </Elements>
                    )}
                </div>
            </div>
        </div>
    );
};

const CampaignFeePaymentForm = ({ feeLabel, loading, setLoading, paidRef, onPaid }) => {
    const stripe = useStripe();
    const elements = useElements();

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!stripe || !elements || paidRef.current) return;

        setLoading(true);
        try {
            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                confirmParams: {
                    return_url: getBillingReturnUrl(),
                },
                redirect: "if_required",
            });

            if (error) {
                showToast("error", error.message || "Payment failed. Please try again.");
                return;
            }

            if (paymentIntent?.status !== "requires_capture" && paymentIntent?.status !== "succeeded") {
                showToast("error", "Payment has not been authorized. Please try again.");
                return;
            }

            paidRef.current = true;
            onPaid(paymentIntent.id);
        } catch (err) {
            showToast("error", "Something went wrong while confirming payment.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form
            onSubmit={handleSubmit}
            className="bg-white/5 p-6 rounded-xl border border-white/10 space-y-4"
        >
            <PaymentElement />
            <button
                type="submit"
                disabled={!stripe || loading}
                className={`w-full mt-6 py-3 rounded-lg text-lg font-bold text-white transition-all ${loading ? "bg-gray-500 cursor-not-allowed" : "bg-[#FA6138] hover:bg-[#e55532]"
                    }`}
            >
                {loading ? (
                    <div className="flex justify-center items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Processing...
                    </div>
                ) : (
                    `Pay ${feeLabel} & Launch Campaign`
                )}
            </button>
        </form>
    );
};
